import { Suspense } from 'react';

import { bindToggle } from 'material-ui-popup-state/core';
import { PopupState } from 'material-ui-popup-state/hooks';
import { OrganizationAvatar } from '@cast/shared-organization-ui';

import { Organization, OrganizationType } from '@cast/types';
import { Box, Skeleton } from '@cast/design-system';

import { useIsViewingEnterpriseOrg } from 'features/organization/hooks/useIsViewingEnterpriseOrg';

type OrganizationMenuTriggerProps = {
  activeOrganization: Organization;
  activeOrganizationParent?: Organization;
  popupState: PopupState;
};

type OrganizationMenuTriggerContentProps = OrganizationMenuTriggerProps & {
  isViewingEnterprise: boolean;
};

const OrganizationMenuTriggerContent = ({
  activeOrganization,
  activeOrganizationParent,
  popupState,
  isViewingEnterprise,
}: OrganizationMenuTriggerContentProps) => {
  const showSingleIcon =
    isViewingEnterprise ||
    activeOrganization.type === OrganizationType.DEFAULT ||
    activeOrganization.type === OrganizationType.ENTERPRISE;

  const displayOrgId =
    isViewingEnterprise && activeOrganizationParent
      ? activeOrganizationParent.id
      : activeOrganization.id;

  return (
    <Box
      {...bindToggle(popupState)}
      display="inline-block"
      data-testid="org-menu-list-btn"
      data-active-org-id={activeOrganization.id}
      sx={{
        '& button:hover': {
          cursor: 'pointer',
        },
        position: 'relative',
      }}
    >
      {showSingleIcon ? (
        <OrganizationAvatar size="sm" organizationId={displayOrgId} />
      ) : (
        <>
          <OrganizationAvatar
            size="sm"
            organizationId={activeOrganizationParent?.id}
          />
          <Box
            sx={{
              position: 'absolute',
              bottom: '-25%',
              right: '-25%',
            }}
          >
            <OrganizationAvatar
              size="2xs"
              organizationId={activeOrganization.id}
            />
          </Box>
        </>
      )}
    </Box>
  );
};

const OrganizationMenuTriggerInner = (props: OrganizationMenuTriggerProps) => {
  const isViewingEnterprise = useIsViewingEnterpriseOrg();

  return (
    <OrganizationMenuTriggerContent
      {...props}
      isViewingEnterprise={isViewingEnterprise}
    />
  );
};

export const OrganizationMenuTrigger = (
  props: OrganizationMenuTriggerProps
) => {
  return (
    // Necessary because the component retrieves an async atom value from useIsViewingEnterpriseOrg
    <Suspense fallback={<Skeleton width={28} height={28} />}>
      <OrganizationMenuTriggerInner {...props} />
    </Suspense>
  );
};
